import { useCallback, useRef, useState } from "react";
import { useVerifyMobileMoneyAccount } from "../../../../hooks/queries/useMerchantQueries";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import { validationSchemas } from "./useAccountForm";

const { msisdn: msisdnValidator } = validationSchemas.mobileMoneyValidators;

export const MOBILE_MONEY_VERIFICATION_STATUS = {
  IDLE: "idle",
  VERIFYING: "verifying",
  VERIFIED: "verified",
  UNVERIFIED: "unverified",
};

const buildKey = (msisdn, provider) => `${msisdn}:${provider}`;

export const useMobileMoneyVerification = ({ setValue }) => {
  const [status, setStatus] = useState(
    MOBILE_MONEY_VERIFICATION_STATUS.IDLE,
  );
  const [message, setMessage] = useState(null);
  const verifiedKeyRef = useRef(null);
  const pendingKeyRef = useRef(null);

  const { mutate, isPending: isVerifying } = useVerifyMobileMoneyAccount();

  const resetVerification = useCallback(() => {
    verifiedKeyRef.current = null;
    pendingKeyRef.current = null;
    setStatus(MOBILE_MONEY_VERIFICATION_STATUS.IDLE);
    setMessage(null);
  }, []);

  const clearIfStale = useCallback(() => {
    if (verifiedKeyRef.current === null) return;
    verifiedKeyRef.current = null;
    setStatus(MOBILE_MONEY_VERIFICATION_STATUS.IDLE);
    setMessage(null);
    setValue("accountName", "");
  }, [setValue]);

  const handleMsisdnChange = useCallback(() => {
    clearIfStale();
  }, [clearIfStale]);

  const handleProviderChange = useCallback(() => {
    clearIfStale();
  }, [clearIfStale]);

  const handleBlur = useCallback(
    (msisdn, provider) => {
      if (!provider) return;
      if (msisdnValidator(msisdn)) return;

      const key = buildKey(msisdn, provider);
      if (verifiedKeyRef.current === key) return;

      pendingKeyRef.current = key;
      setStatus(MOBILE_MONEY_VERIFICATION_STATUS.VERIFYING);
      setMessage(null);

      mutate(
        { msisdn, provider },
        {
          onSuccess: (data) => {
            if (pendingKeyRef.current !== key) return;

            if (data.success) {
              verifiedKeyRef.current = key;
              setStatus(MOBILE_MONEY_VERIFICATION_STATUS.VERIFIED);
              setMessage(null);
              setValue("accountName", data.recipientName || "");
            } else {
              verifiedKeyRef.current = null;
              setStatus(MOBILE_MONEY_VERIFICATION_STATUS.UNVERIFIED);
              setMessage(data.message || "Verification failed");
              setValue("accountName", "");
            }
          },
          onError: (error) => {
            if (pendingKeyRef.current !== key) return;

            verifiedKeyRef.current = null;
            setStatus(MOBILE_MONEY_VERIFICATION_STATUS.UNVERIFIED);
            setMessage(
              extractAxiosErrorMessage(error, "Could not verify account"),
            );
            setValue("accountName", "");
          },
        },
      );
    },
    [mutate, setValue],
  );

  return {
    handleBlur,
    handleMsisdnChange,
    handleProviderChange,
    resetVerification,
    isVerifying,
    isVerified: status === MOBILE_MONEY_VERIFICATION_STATUS.VERIFIED,
    isUnverified: status === MOBILE_MONEY_VERIFICATION_STATUS.UNVERIFIED,
    message,
  };
};

export default useMobileMoneyVerification;
